import React, { useEffect, useMemo, useRef, useState } from 'react'
import { hoyLocal } from '../utils/fecha'
import {
  CCol, CRow, CCard, CCardBody, CCardTitle, CButton, CAlert, CForm,
  CFormInput, CFormLabel, CFormSelect, CBadge, CModal, CModalHeader, CModalTitle, CModalBody,
  CModalFooter, CInputGroup, CInputGroupText,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilBank, cilCalculator, cilDescription, cilUser, cilCheckCircle, cilX,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn, WwRowAction } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  { key: 'cierreDiarioNro', title: 'Nro', type: 'number' },
  { key: 'cierreDiarioFecha', title: 'Fecha', type: 'date' },
  { key: 'cierreDiarioHora', title: 'Hora' },
  { key: 'zonaNombre', title: 'Ruta' },
  { key: 'cierreDiarioAbonosRuta', title: 'Abonos', type: 'currency' },
  { key: 'cierreDiarioPrestamosRuta', title: 'Prestamos', type: 'currency' },
  { key: 'cierreDiarioGastosRuta', title: 'Gastos', type: 'currency' },
  { key: 'cajaNombre', title: 'Caja' },
  // Captions EXACTOS del grid GX (columnsselector de cierrediarioww.cs:2583-2596):
  // Nro, Fecha, Hora, Ruta, Caja, Base, Abonos, Prestamos, Gastos, Cierra Con,
  // Cuadre, Entregó, Utilidad, Cobrador. Las columnas extra de la réplica
  // (Diario Abonos/Renovaciones/Gastos/Faltantes) no existen en el grid GX.
  { key: 'cierreDiarioCaja', title: 'Cierra Con', type: 'currency' },
  { key: 'cobrador', title: 'Cobrador' },
  { key: 'cierreDiarioBaseAnterior', title: 'Base', type: 'currency' },
  { key: 'cierreDiarioAbonos', title: 'Diario Abonos', type: 'currency' },
  { key: 'cierreDiarioRenovaciones', title: 'Diario Renovaciones', type: 'currency' },
  { key: 'cierreDiarioGastos', title: 'Diario Gastos', type: 'currency' },
  { key: 'totalEntrega', title: 'Cuadre', type: 'currency' },
  { key: 'cierreDiarioDineroEntregado', title: 'Entregó', type: 'currency' },
  { key: 'cierreDiarioFaltantes', title: 'Diario Faltantes', type: 'currency' },
]

// Los montos se muestran con la fuente única utils/moneda.ts (fmtMoneda:
// símbolo configurado por empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99" —
// arptcortecajadiario.cs:184). [RGM] 2026-10-02 (F)

/** Tarjeta del diseño del cierre: título + filas label/monto (opcional color). */
function Tarjeta({
  titulo, icono, color, children,
}: {
  titulo: string
  icono: any
  color?: string
  children: React.ReactNode
}) {
  return (
    <CCard className="h-100 shadow-sm">
      <CCardBody>
        <CCardTitle className="d-flex align-items-center gap-2 mb-3" style={{ color: color ?? 'inherit' }}>
          <CIcon icon={icono} size="lg" />
          <span className="fw-semibold">{titulo}</span>
        </CCardTitle>
        {children}
      </CCardBody>
    </CCard>
  )
}

function Fila({ label, valor, color }: { label: string; valor: string; color?: string }) {
  return (
    <div className="d-flex justify-content-between py-1 border-bottom border-light">
      <span className="text-body-secondary">{label}</span>
      <span className="fw-semibold" style={{ color: color ?? 'inherit' }}>{valor}</span>
    </div>
  )
}

const CierrePage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // ── Filtros rápidos de la grilla (rutas/cobradores — los más usados) ──
  const [filtroRuta, setFiltroRuta] = useState('')
  const [filtroCobrador, setFiltroCobrador] = useState('')
  const [rutas, setRutas] = useState<string[]>([])
  // Lista completa de rutas (zonaCodigo + zonaNombre) para el COMBO de Ruta
  // del formulario de insert (dynZonaCodigo/Combo_zonacodigo — cierrediario.cs:3524-3529).
  const [rutasFull, setRutasFull] = useState<any[]>([])
  const [cobradores, setCobradores] = useState<string[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [seleccionado, setSeleccionado] = useState<any | null>(null)
  // Preview del cierre (route-closures/preview): precarga los campos que el
  // BC calcula en el modal réplica del form GX (cierrediario.cs).
  const [preview, setPreview] = useState<any | null>(null)
  const { aviso, setAviso } = usePendiente()
  const [form, setForm] = useState({
    empresaCodigo: empresa, zonaCodigo: '', cobradorNombre: '',
    fecha: hoyLocal(), baseAnterior: '', dineroEntregado: '',
  })

  // Dominio de los filtros (mismos endpoints que la barra de Préstamos:
  // GET /rutas y GET /cobradores — prestamowwgetfilterdata.cs).
  useEffect(() => {
    api.get<any[]>('/rutas?empresa=' + empresa)
      .then((r) => {
        setRutasFull(r)
        setRutas([...new Set(r.map((x: any) => String(x.zonaNombre ?? '').trim()).filter(Boolean))])
      })
      .catch(() => undefined)
    api.get<any[]>('/cobradores?empresa=' + empresa)
      .then((cb) => setCobradores([...new Set(cb.map((x: any) => String(x.usuario ?? '').trim()).filter(Boolean))]))
      .catch(() => undefined)
  }, [empresa])

  // Combo de Ruta del form (dynZonaCodigo + Combo_zonacodigo — cierrediario.cs:3524-3529,
  // datalist CierreDiarioLoadDVCombo): al elegir la ruta se recargan los campos
  // calculados del cierre (preview) — replica del comportamiento del panel GX.
  const rutaSeleccionada = rutasFull.find((r) => String(r.zonaCodigo) === form.zonaCodigo)
  const seleccionarRuta = (zonaCodigo: string) => {
    const r = rutasFull.find((x) => String(x.zonaCodigo) === zonaCodigo)
    setForm((f) => ({
      ...f,
      zonaCodigo,
      // Base y Cobrador salen de la RUTA (cierrediario_bc.cs:334 BaseAnterior =
      // ZonaBase; cobrador de la Zona) — el form GX los muestra deshabilitados
      // (cierrediario.cs:3499/3501 Enabled = 0). Pedido Rafael 2026-09-30.
      cobradorNombre: r ? String(r.cobradorNombre ?? '').trim() : '',
      baseAnterior: r ? String(r.zonaBase ?? '') : '',
    }))
    setPreview(null)
    if (zonaCodigo === '') return
    api.get<any>(`/route-closures/preview?routeId=${zonaCodigo}`)
      .then(setPreview)
      .catch((e: any) => setError(e.message))
  }

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/cierres?empresa=${empresa}`)
      .then((data) => {
        setRows(data)
        // Selección inicial: el cierre más reciente (la grilla ordena por Nro desc).
        if (data.length > 0 && !seleccionado) setSeleccionado(data[0])
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  // Al abrir el modal: precargar el preview del cierre de la ruta (si hay)
  // para llenar los campos calculados del form GX (replica cierrediario.cs).
    // Filas filtradas por ruta/cobrador (client-side, como Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroRuta && String(row.zonaNombre ?? '').trim() !== filtroRuta) return false
    if (filtroCobrador && String(row.cobrador ?? '').trim() !== filtroCobrador) return false
    return true
  }), [rows, filtroRuta, filtroCobrador])

  const filtrosActivos = [
    filtroRuta ? { clave: 'ruta', label: `Ruta: ${filtroRuta}` } : null,
    filtroCobrador ? { clave: 'cobrador', label: `Cobrador: ${filtroCobrador}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]

  const limpiarFiltros = () => { setFiltroRuta(''); setFiltroCobrador('') }

const abrirModal = () => {
    setError('')
    setPreview(null)
    setAbierto(true)
    if (form.zonaCodigo === '') return
    api.get<any>(`/route-closures/preview?routeId=${form.zonaCodigo}`)
      .then(setPreview)
      .catch((e: any) => setError(e.message))
  }

  const crear = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    // El combo GX de Ruta no tiene opción vacía (Combo_zonacodigo_Emptyitem = 0,
    // cierrediario.cs:3525): exige una ruta antes de cerrar el día.
    if (form.zonaCodigo === '') {
      setError('Seleccione la Ruta.')
      return
    }
    try {
      await api.post('/cierres', {
        ...form,
        zonaCodigo: Number(form.zonaCodigo),
        baseAnterior: Number(form.baseAnterior), dineroEntregado: Number(form.dineroEntregado),
      })
      setAbierto(false)
      setSeleccionado(null)
      cargar()
    } catch (err: any) {
      setError(err.message)
    }
  }

  // 'Corte' e 'Imprimir' — el grid GX (cierrediarioww.cs:2417-2420) llama a
  // los reportes PDF:
  //   - 'Corte'    → arptcortecajadiario (parm InoviceDate = fecha del cierre)
  //   - 'Imprimir' → arptcierrediario   (parm CierreDiarioNro)
  // Réplica con PDFs REALES (QuestPDF: CorteCajaPdfBuilder /
  // CierreDiarioPdfBuilder) que se abren directo en pestaña nueva con el
  // helper global api.abrirPdf (mismo patrón del resto de la app). Se retiró
  // la vista de datos en modal + window.print del prototipo (pedido Rafael
  // 2026-10-15: "no está mandando el PDF... y el corte igual").
  const corte = async (fila: any) => {
    setError('')
    const fecha = String(fila.cierreDiarioFecha ?? '').slice(0, 10)
    try {
      await api.abrirPdf(`/cierres/corte.pdf?empresa=${empresa}&fecha=${fecha}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el corte')
    }
  }

  const imprimir = async (fila: any) => {
    setError('')
    try {
      await api.abrirPdf(`/cierres/${fila.cierreDiarioNro}/imprimir.pdf?empresa=${empresa}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el cierre')
    }
  }
  // Acciones: Corte e Imprimir van VISIBLES en la fila (inline-end — botones
  // al final, como las operativas de Rutas) y solo Eliminar queda en el menú ⋮
  // (pedido Rafael 2026-09-30).
  const rowActions: WwRowAction[] = [
    { label: 'Corte', onClick: corte, placement: 'inline-end', icon: 'cilSpreadsheet' },
    { label: 'Imprimir', onClick: imprimir, placement: 'inline-end', icon: 'cilPrint' },
    // [RGM] 2026-10-01 (pedido Rafael): sin menú ⋮ — Eliminar pasa a icono
    // directo de la fila (era el único ítem que quedaba en el menú). Lambda:
    // `eliminar` se declara más abajo (evita el uso antes de la declaración).
    { label: 'Eliminar', onClick: (fila) => eliminar(fila), placement: 'inline-end', icon: 'cilTrash', destructive: true },
  ]

  // Eliminar — el grid GX SÍ lo permite: cierrediarioww.cs:2684-2686
  // ('DO DELETE' → webobject cierrediario Mode="DLT"). El BC delete()
  // (cierrediario_bc.cs:712-741) borra SIN controles de referencia
  // (OnDeleteControls087 solo recalcula fórmulas de pantalla).
  const eliminar = (fila: any) => {
    if (!window.confirm(`¿Eliminar el cierre ${fila.cierreDiarioNro}?`)) return
    api.delete(`/cierres/${fila.cierreDiarioNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Cierre eliminado.')
        setSeleccionado(null)
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  // El grid GX de cierres NO tiene acción Modificar (cierrediarioww.cs: solo
  // Corte/Imprimir/Delete): un cierre creado ya no se puede editar — sin botón
  // Editar en la grilla (pedido Rafael 2026-09-30).

  const [totalReg, setTotalReg] = useState(rows.length)

  // Modal de DETALLE del cierre: las 5 tarjetas GX (cierrediario.cs) se abren
  // al hacer clic en una fila de la grilla — la grilla queda limpia.
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const abrirDetalle = (fila: any) => {
    setSeleccionado(fila)
    setDetalleAbierto(true)
  }

  const detalle = useMemo(() => {
    const c = seleccionado
    if (!c) return null
    const faltantes = typeof c.cierreDiarioFaltantes === 'number' ? c.cierreDiarioFaltantes : 0
    return (
      <CRow className="g-3">
        <CCol md={3}>
          <Tarjeta titulo="Identificación" icono={cilDescription}>
            <Fila label="Nro" valor={String(c.cierreDiarioNro ?? '—')} />
            <Fila label="Fecha" valor={String(c.cierreDiarioFecha ?? '—')} />
            <Fila label="Hora" valor={String(c.cierreDiarioHora ?? '—')} />
          </Tarjeta>
        </CCol>
        <CCol md={3}>
          <Tarjeta titulo="Todo la Ruta" icono={cilCalculator} color="#078bcd">
            <Fila label="Abonos" valor={fmtMoneda(c.cierreDiarioAbonosRuta)} />
            <Fila label="Prestamos" valor={fmtMoneda(c.cierreDiarioPrestamosRuta)} color="#b02a37" />
            <Fila label="Gastos" valor={fmtMoneda(c.cierreDiarioGastosRuta)} color="#b02a37" />
          </Tarjeta>
        </CCol>
        <CCol md={3}>
          <Tarjeta titulo="Caja" icono={cilBank} color="#078bcd">
            <Fila label="Caja" valor={String(c.cajaNombre ?? '—')} />
            <Fila label="Cierra Con" valor={fmtMoneda(c.cierreDiarioCaja)} />
            <Fila label="Ruta" valor={String(c.zonaNombre ?? '—')} />
          </Tarjeta>
        </CCol>
        <CCol md={3}>
          <Tarjeta titulo="Solo el Cobrador" icono={cilUser} color="#1e9e6a">
            <Fila label="Cobrador" valor={String(c.cobrador ?? '—')} />
            <Fila label="Base" valor={fmtMoneda(c.cierreDiarioBaseAnterior)} />
            <Fila label="Diario Abonos" valor={fmtMoneda(c.cierreDiarioAbonos)} />
            <Fila label="Diario Renovaciones" valor={fmtMoneda(c.cierreDiarioRenovaciones)} color="#b02a37" />
            <Fila label="Diario Gastos" valor={fmtMoneda(c.cierreDiarioGastos)} color="#b02a37" />
          </Tarjeta>
        </CCol>
        <CCol md={12}>
          <Tarjeta titulo="Resultado" icono={cilCheckCircle} color="#1e9e6a">
            <CRow className="align-items-center g-3">
              <CCol md={3}><Fila label="Cuadre" valor={fmtMoneda(c.totalEntrega)} color="#1e9e6a" /></CCol>
              <CCol md={3}><Fila label="Entregó" valor={fmtMoneda(c.cierreDiarioDineroEntregado)} /></CCol>
              <CCol md={3}>
                <Fila
                  label="Diario Faltantes"
                  valor={fmtMoneda(c.cierreDiarioFaltantes)}
                  color={faltantes > 0 ? '#b02a37' : '#1e9e6a'}
                />
              </CCol>
            </CRow>
          </Tarjeta>
        </CCol>
      </CRow>
    )
  }, [seleccionado])

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Cierre Diario</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <WwGrid
        filters={
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
            <CFormSelect className="w-auto" value={filtroRuta} onChange={(e) => setFiltroRuta(e.target.value)}>
              <option value="">Todas las Rutas</option>
              {rutas.map((r) => <option key={r} value={r}>{r}</option>)}
            </CFormSelect>
            <CFormSelect className="w-auto" value={filtroCobrador} onChange={(e) => setFiltroCobrador(e.target.value)}>
              <option value="">Todos los Cobradores</option>
              {cobradores.map((cb) => <option key={cb} value={cb}>{cb}</option>)}
            </CFormSelect>
            {filtrosActivos.length > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-1">
                {filtrosActivos.map((f) => (
                  <CBadge key={f.clave} color="secondary" className="d-inline-flex align-items-center gap-1 fw-normal">
                    {f.label}
                    <CIcon icon={cilX} size="sm" role="button" style={{ cursor: 'pointer' }}
                      onClick={() => { if (f.clave === 'ruta') setFiltroRuta(''); if (f.clave === 'cobrador') setFiltroCobrador('') }} />
                  </CBadge>
                ))}
                <CButton size="sm" color="outline-secondary" onClick={limpiarFiltros}>Limpiar filtros</CButton>
              </div>
            )}
          </div>
        }
        title="Cierre Diario"
        columns={columnas}
        rows={visibleRows}
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirModal}
        onRowClick={abrirDetalle}
        rowActions={rowActions}
        busquedaPlaceholder="Buscar por cierre, ruta o cobrador..." 
      />

      {/* Modal de DETALLE: las 5 tarjetas GX (cierrediario.cs) al hacer clic
          en una fila de la grilla. */}
      <CModal visible={detalleAbierto} onClose={() => setDetalleAbierto(false)} size="xl">
        <CModalHeader>
          <CModalTitle className="d-flex align-items-center gap-2">
            <CIcon icon={cilDescription} size="lg" />
            <span>Detalle del Cierre · {seleccionado?.cierreDiarioNro ?? ''}</span>
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {detalle}
        </CModalBody>
        <CModalFooter>
          {/* Imprimir/Descargar el cierre desde el DETALLE (pedido Rafael):
              abre el informe del cierre (el mismo recibo del grid). */}
          <CButton
            color="primary"
            disabled={!seleccionado}
            onClick={() => {
              if (!seleccionado) return
              setDetalleAbierto(false)
              imprimir(seleccionado)
            }}
          >
            <CIcon icon={cilDescription} className="me-1" />
            Imprimir cierre
          </CButton>
          <CButton color="secondary" onClick={() => setDetalleAbierto(false)}>Cerrar</CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={abierto} onClose={() => setAbierto(false)} size="xl">
        <CModalHeader>
          <CModalTitle className="d-flex align-items-center gap-2">
            <CIcon icon={cilDescription} size="lg" />
            <span>Cierre Diario</span>
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <CForm ref={formRef} onSubmit={crear}>
            {/* Réplica COMPLETA del formulario GX (cierrediario.cs): los 22
                campos en el orden del panel web. Los que el BC calcula
                (Abonos/Prestamos/Gastos Ruta, Utilidad, Caja, Diario
                Abonos/Renovaciones/Gastos, Total, Faltantes) se muestran
                readonly: el backend los arma al confirmar. */}
            <CRow className="g-3">
              <CCol md={4}>
                <Tarjeta titulo="Identificación" icono={cilDescription}>
                  <CFormLabel className="small text-body-secondary">Diario Nro</CFormLabel>
                  <CFormInput disabled placeholder="Consecutivo" />
                  <CFormLabel className="small text-body-secondary mt-2">Diario Fecha</CFormLabel>
                  {/* Fecha del sistema NO editable en el insert
                      (cierrediario.cs:3533 edtCierreDiarioFecha_Enabled = 0). */}
                  <CFormInput type="date" disabled value={form.fecha} />
                  <CFormLabel className="small text-body-secondary mt-2">Diario Hora</CFormLabel>
                  <CFormInput disabled placeholder="Hora del sistema" />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Ruta" icono={cilCalculator} color="#078bcd">
                  <CFormLabel className="small text-body-secondary">Ruta Codigo</CFormLabel>
                  {/* Ruta en COMBO de Zona (dynZonaCodigo/Combo_zonacodigo —
                      cierrediario.cs:3524-3529, datalist CierreDiarioLoadDVCombo). */}
                  <CFormSelect value={form.zonaCodigo} onChange={(e) => seleccionarRuta(e.target.value)}>
                    <option value="">(Seleccionar Ruta)</option>
                    {rutasFull.map((r) => (
                      <option key={r.zonaCodigo} value={String(r.zonaCodigo)}>{r.zonaNombre}</option>
                    ))}
                  </CFormSelect>
                  <CFormLabel className="small text-body-secondary mt-2">Ruta Nombre</CFormLabel>
                  <CFormInput disabled value={rutaSeleccionada?.zonaNombre ?? ''} placeholder="Zona de la ruta" />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Todo la Ruta" icono={cilBank} color="#078bcd">
                  <CFormLabel className="small text-body-secondary">Abonos Ruta</CFormLabel>
                  <CFormInput disabled value={preview?.abonosRuta ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Prestamos Ruta</CFormLabel>
                  <CFormInput disabled value={preview?.loansAmount ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Gastos Ruta</CFormLabel>
                  <CFormInput disabled value={preview?.gastosRuta ?? ''} />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Utilidad" icono={cilCheckCircle} color="#1e9e6a">
                  <CFormLabel className="small text-body-secondary">Utilidad Bruta</CFormLabel>
                  <CFormInput disabled value={preview?.interesesAmount ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Utilidad Neta</CFormLabel>
                  {/* GX: AV17Bruta = Utilidad del día − Gastos Ruta
                      (cierrediario_bc.cs:358) — con gastos 0 la Neta = la Bruta.
                      Antes mostraba el CAPITAL (negativo) — reportado Rafael
                      2026-09-30 (ruta 2: bruta 10 / neta mostraba −10). */}
                  <CFormInput disabled value={preview ? String(Number(preview.interesesAmount ?? 0) - Number(preview.gastosRuta ?? 0)) : ''} />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Caja" icono={cilBank} color="#078bcd">
                  <CFormLabel className="small text-body-secondary">Caja Nro</CFormLabel>
                  <CFormInput disabled value={preview?.cajaNro ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Caja Nombre</CFormLabel>
                  <CFormInput disabled value={preview?.cajaNombre ?? ''} />
                  {/* Caption del EDIT del cierre según GX: "Cierra Con" (pedido
                      Rafael 2026-09-30). */}
                  <CFormLabel className="small text-body-secondary mt-2">Cierra Con</CFormLabel>
                  <CFormInput disabled value={preview?.cajaTotal ?? ''} />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Cobrador" icono={cilUser} color="#1e9e6a">
                  <CFormLabel className="small text-body-secondary">Cobrador Codigo</CFormLabel>
                  <CFormInput disabled value={preview?.cobradorCodigo ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Cobrador Usuario</CFormLabel>
                  {/* Cobrador y Base salen de la RUTA elegida — deshabilitados
                      como en el form GX (cierrediario.cs:3499/3501 Enabled = 0). */}
                  <CFormInput disabled value={form.cobradorNombre} />
                  {/* Caption del EDIT del cierre según GX: "Base" (pedido
                      Rafael 2026-09-30). */}
                  <CFormLabel className="small text-body-secondary mt-2">Base</CFormLabel>
                  <CFormInput disabled value={form.baseAnterior} />
                </Tarjeta>
              </CCol>
              <CCol md={4}>
                <Tarjeta titulo="Solo el Cobrador" icono={cilCalculator} color="#078bcd">
                  <CFormLabel className="small text-body-secondary">Diario Abonos</CFormLabel>
                  <CFormInput disabled value={preview?.paymentsAmount ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Diario Renovaciones</CFormLabel>
                  <CFormInput disabled value={preview?.renewalsAmount ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Diario Gastos</CFormLabel>
                  <CFormInput disabled value={preview?.expensesAmount ?? ''} />
                </Tarjeta>
              </CCol>
              <CCol md={8}>
                <Tarjeta titulo="Resultado" icono={cilCheckCircle} color="#1e9e6a">
                  <CFormLabel className="small text-body-secondary">Total Entrega</CFormLabel>
                  <CFormInput disabled value={preview?.expectedCash ?? ''} />
                  <CFormLabel className="small text-body-secondary mt-2">Dinero Entregado</CFormLabel>
                  {/* [RGM] 2026-10-02 (moneda global): prefijo + miles. */}
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={form.dineroEntregado}
                      onValueChange={(v) => setForm({ ...form, dineroEntregado: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                  <CFormLabel className="small text-body-secondary mt-2">Diario Faltantes</CFormLabel>
                  <CFormInput disabled value={preview?.expectedCash != null && form.dineroEntregado !== '' ? Number(preview.expectedCash) - Number(form.dineroEntregado) : ''} />
                </Tarjeta>
              </CCol>
            </CRow>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setAbierto(false)}>Cancelar</CButton>
          <CButton color="primary" type="button" onClick={() => formRef.current?.requestSubmit()}>
            <CIcon icon={cilCheckCircle} className="me-1" />
            Cerrar Día
          </CButton>
        </CModalFooter>
      </CModal>

    </>
  )
}

export default CierrePage
